import React, { useState, useEffect } from "react";
import MainCalculatorsList from "../FoodCalculator/MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة الفطائر المثالية - طريقة عمل البان كيك المقرمش والهش";
const DESCRIPTION =
  "حاسبة الفطائر المثالية (البان كيك) المجانية - احسب كمية المكونات الدقيقة لعمل فطائر هشة ولذيذة. أدخل عدد الفطائر أو الحجم واحصل على وصفة البان كيك المثالية فوراً.";
const KEYWORDS =
  "حاسبة الفطائر, طريقة عمل البان كيك, مكونات البان كيك, حاسبة وصفات الطعام, بان كيك هش, مكونات الفطائر, pancake calculator, perfect pancake recipe";
const OG_URL = "https://do-calculate.com/calculator/ar/perfect-pancake/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة الفطائر المثالية",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة الفطائر المثالية",
        item: OG_URL,
      },
    ],
  },
];

function PerfectPancakeCalculator() {
  // --- States ---
  const [pancakeCount, setPancakeCount] = useState(8);
  const [pancakeSize, setPancakeSize] = useState(10); // Diameter in cm
  const [pancakeThickness, setPancakeThickness] = useState(1); // cm
  const [servingSize, setServingSize] = useState(2); // Pancakes per person

  const [result, setResult] = useState(null);

  // --- Calculations ---
  // Ratios based on standard perfect pancake logic:
  // For ~100ml of batter (approx 1 large pancake of 10cm diam, 1cm thick):
  // Flour: 62.5g, Milk: 90ml, Egg: 0.5, Butter: 7.5g, Sugar: 5g, Baking Powder: 3g, Salt: 0.5g

  const calculateIngredients = () => {
    // Volume of one pancake (cylinder: π * r² * h)
    const radius = pancakeSize / 2;
    const volumePerPancake = Math.PI * Math.pow(radius, 2) * pancakeThickness; // cm³ (ml)

    // Scale factor based on a reference pancake of 10cm diam, 1cm thick (~78.5 ml)
    const referenceVolume = Math.PI * Math.pow(5, 2) * 1; 
    const scaleFactor = (volumePerPancake / referenceVolume) * pancakeCount;

    const flour = 62.5 * scaleFactor;
    const milk = 90 * scaleFactor;
    const eggs = 0.5 * scaleFactor;
    const butter = 7.5 * scaleFactor;
    const sugar = 5 * scaleFactor;
    const bakingPowder = 3 * scaleFactor;
    const salt = 0.5 * scaleFactor;

    setResult({
      flour,
      milk,
      eggs: Math.ceil(eggs * 10) / 10, // precision to 0.1
      butter,
      sugar,
      bakingPowder,
      salt,
      totalVolume: volumePerPancake * pancakeCount,
      peopleServed: Math.floor(pancakeCount / servingSize)
    });
  };

  useEffect(() => {
    calculateIngredients();
  }, [pancakeCount, pancakeSize, pancakeThickness, servingSize]);

  const handleClear = () => {
    setPancakeCount(8);
    setPancakeSize(10);
    setPancakeThickness(1);
    setServingSize(2);
    setResult(null);
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة الفطائر المثالية (البان كيك)</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                تساعدك <strong>حاسبة الفطائر المثالية</strong> على تحديد المقادير الدقيقة لتحضير بان كيك مثالي بناءً على عدد الفطائر التي ترغب في صنعها وحجمها. سواء كنت تحضر إفطاراً لشخصين أو وليمة للعائلة، تضمن لك هذه الأداة توازناً مثالياً بين الدقيق، الحليب، والبيض.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>حدد تفاصيل الفطائر</h2>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label >عدد الفطائر المطلوب</label>
                  <input
                    type="number"
                    className="form-control"
                    value={pancakeCount}
                    onChange={(e) => setPancakeCount(parseFloat(e.target.value) || 0)}
                    min="1"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label >قطر الفطيرة الواحدة (سم)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={pancakeSize}
                    onChange={(e) => setPancakeSize(parseFloat(e.target.value) || 0)}
                    min="1"
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-sm-12 col-md-6">
                  <label >سُمك الفطيرة (سم)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={pancakeThickness}
                    onChange={(e) => setPancakeThickness(parseFloat(e.target.value) || 0)}
                    min="0.1"
                    step="0.1"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label >كم فطيرة لكل شخص؟</label>
                  <input
                    type="number"
                    className="form-control"
                    value={servingSize}
                    onChange={(e) => setServingSize(parseFloat(e.target.value) || 1)}
                    min="1"
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  إعادة ضبط
                </button>
              </div>

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>المكونات المطلوبة:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>المكون</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>الكمية</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>الدقيق (طحين)</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.flour.toFixed(1)} جرام</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>الحليب</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.milk.toFixed(1)} مل</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>البيض</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.eggs} حبة</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>الزبدة المذابة</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.butter.toFixed(1)} جرام</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>السكر</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.sugar.toFixed(1)} جرام</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>بيكنج باودر</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.bakingPowder.toFixed(1)} جرام</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>ملح</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.salt.toFixed(1)} جرام</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>ملخص الوجبة</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          <li>🥞 <strong>إجمالي عدد الفطائر:</strong> {pancakeCount} فطيرة</li>
                          <li>👥 <strong>تكفي لـ:</strong> {result.peopleServed} أشخاص تقريباً</li>
                          <li>🥣 <strong>إجمالي حجم الخليط:</strong> {result.totalVolume.toFixed(0)} مل</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>أسرار البان كيك المثالي</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  للحصول على أفضل نتيجة باستخدام المقادير الموضحة في الحاسبة، اتبع النصائح التالية:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>لا تبالغ في الخلط:</strong> اخلط المكونات حتى تختفي التكتلات الكبيرة فقط؛ الخلط الزائد يجعل الفطائر قاسية.</li>
                  <li><strong>راحة الخليط:</strong> اترك العجينة ترتاح لمدة 5-10 دقائق قبل الطهي لتفعيل البيكنج باودر.</li>
                  <li><strong>حرارة المقلاة:</strong> استخدم مقلاة غير لاصقة على نار متوسطة.</li>
                  <li><strong>علامة النضج:</strong> اقلب الفطيرة عندما تظهر فقاعات على السطح وتصبح الحواف جافة قليلاً.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>كيف تعمل حاسبة الفطائر؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  تستخدم الحاسبة صيغة رياضية تعتمد على حجم الفطيرة (القطر والارتفاع) لتقدير كمية الخليط السائل (Batter) المطلوبة. ثم تقوم بتقسيم هذا الحجم إلى نسب مئوية دقيقة من المكونات الجافة والسائلة بناءً على معايير الشيفات العالمية لأفضل قوام هش.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PerfectPancakeCalculator;